import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, AlertTriangle, X, Clock, ChevronRight } from 'lucide-react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';

/**
 * NotificationBadge Component
 * Displays a bell icon with unread notification count badge
 * Shows a dropdown preview of recent notifications on hover/click
 */
const NotificationBadge = () => {
  const [badgeData, setBadgeData] = useState({
    unread_count: 0,
    critical_count: 0,
    high_count: 0,
    overdue_tasks: 0,
    has_urgent: false
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBadgeCount = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const data = await apiFetch(`${API}/notifications/badge-count`);
    if (data?._error) {
      console.error('Failed to fetch notification count:', data.message);
      return;
    }
    setBadgeData(data);
  }, []);

  const fetchRecentNotifications = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    const data = await apiFetch(`${API}/notifications?limit=5&include_read=false`);
    if (data?._error) {
      console.error('Failed to fetch recent notifications:', data.message);
    } else {
      setRecentNotifications(data.notifications || []);
    }
    setLoading(false);
  }, [loading]);

  // Poll for badge count every 60 seconds
  useEffect(() => {
    fetchBadgeCount();
    const interval = setInterval(fetchBadgeCount, 60000);
    return () => clearInterval(interval);
  }, [fetchBadgeCount]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      fetchRecentNotifications();
    }
  }, [showDropdown, fetchRecentNotifications]);

  const handleMarkAsRead = async (notificationId, e) => {
    e.preventDefault();
    e.stopPropagation();
    const data = await apiFetch(`${API}/notifications/mark-read`, {
      method: 'POST',
      body: { notification_ids: [notificationId] }
    });
    if (data?._error) {
      console.error('Failed to mark notification as read:', data.message);
      return;
    }
    fetchBadgeCount();
    fetchRecentNotifications();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-500 bg-red-50';
      case 'high': return 'text-amber-500 bg-amber-50';
      case 'medium': return 'text-blue-500 bg-blue-50';
      default: return 'text-slate-500 bg-slate-50';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'overdue_task': return <Clock size={14} className="text-red-500" />;
      case 'deadline': return <AlertTriangle size={14} className="text-amber-500" />;
      default: return <Bell size={14} className="text-slate-500" />;
    }
  };

  const totalCount = badgeData.unread_count;
  const hasUrgent = badgeData.has_urgent || badgeData.critical_count > 0;

  return (
    <div className="relative" data-testid="notification-badge-container">
      {/* Bell Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        className={`relative flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
          hasUrgent 
            ? 'text-red-600 hover:text-red-700 hover:bg-red-50' 
            : 'text-slate-600 hover:text-emerald-700 hover:bg-emerald-50'
        }`}
        data-testid="notification-bell-btn"
      >
        <div className="relative">
          <Bell size={18} className={hasUrgent ? 'animate-pulse' : ''} />
          {totalCount > 0 && (
            <span 
              className={`absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full ${
                hasUrgent ? 'bg-red-500' : 'bg-emerald-500'
              }`}
              data-testid="notification-count-badge"
            >
              {totalCount > 99 ? '99+' : totalCount}
            </span>
          )}
        </div>
        <span className="hidden sm:inline">Alerts</span>
      </button>

      {/* Dropdown Preview */}
      {showDropdown && (
        <div 
          className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-slate-900">Notifications</h4>
              <p className="text-xs text-slate-500">
                {totalCount} unread{badgeData.overdue_tasks > 0 && ` • ${badgeData.overdue_tasks} overdue tasks`}
              </p>
            </div>
            {badgeData.critical_count > 0 && (
              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                {badgeData.critical_count} Critical
              </span>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-80 overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No new notifications</p>
              </div>
            ) : (
              recentNotifications.map((notif, idx) => (
                <div
                  key={notif._id || idx}
                  className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
                    !notif.read ? 'bg-blue-50/50' : ''
                  }`}
                  onClick={() => window.location.href = notif.metadata?.action_url || '/notifications'}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-lg ${getPriorityColor(notif.priority)}`}>
                      {getTypeIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm font-medium truncate ${!notif.read ? 'text-slate-900' : 'text-slate-600'}`}>
                          {notif.title}
                        </p>
                        {!notif.read && (
                          <button
                            onClick={(e) => handleMarkAsRead(notif._id, e)}
                            className="p-1 hover:bg-slate-200 rounded transition-colors"
                            title="Mark as read"
                          >
                            <X size={12} className="text-slate-400" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{notif.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {notif.created_at_str || new Date(notif.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <Link
            to="/notifications"
            className="block px-4 py-3 bg-slate-50 text-center text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-slate-100 transition-colors"
            data-testid="view-all-notifications-link"
          >
            View All Notifications
            <ChevronRight size={14} className="inline ml-1" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default NotificationBadge;
